<template>
  <div class="mgx_bar_item purple" :class="{ disabled: !props.isEnabled }" @mouseenter="emits('mouseEnter')" @mouseleave="emits('mouseLeave')">
    <div v-if="props.isSupportSingleKeyDeadZone && magnetSet.isShowSingleKeyDeadZone.value && magnetSet.selectedKeys.value.length == 0" class="progress_overlay" @mousedown="emits('selectCheck')"></div>
    <div class="visual_wrapper">
      <div class="bar_track">
        <div class="bar_fill purple top" v-if="showTop" :style="{ height: getAdaptiveFillHeight(Top) }"></div>
        <div class="bar_fill purple bottom" v-if="showBottom" :style="{ height: getAdaptiveFillHeight(Bottom) }"></div>
      </div>

      <!-- Top Deadzone Slider -->
      <input
        v-if="showTop"
        type="range"
        @input="handleInput"
        @change="emits('change')"
        @wheel="wheelEvent($event, 'top_dead_zone')"
        class="trip_range purple_thumb top_dead_zone"
        style="transform: translate(-50%, -50%) rotate(90deg); z-index: 12"
        v-model.number="Top"
        :max="travel_config.maxTravel"
        min="0"
        :step="props.step"
      />

      <!-- Bottom Deadzone Slider -->
      <input
        v-if="showBottom"
        type="range"
        @input="handleInput"
        @change="emits('change')"
        @wheel="wheelEvent($event, 'bottom_dead_zone')"
        class="trip_range purple_thumb bottom_dead_zone"
        style="transform: translate(-50%, -50%) rotate(-90deg); z-index: 12"
        v-model.number="Bottom"
        :max="travel_config.maxTravel"
        min="0"
        :step="props.step"
      />
    </div>

    <div class="bar_label_group">
      <div class="bar_label">{{ props.isSupportSingleKeyDeadZone ? $t('magnet_dead_zone_single_key') : $t('magnet_dead_zone_global') }}</div>
      <div class="bar_sub_label">
        <template v-if="showTop">
          <input v-model="deadZoneTopMM" maxlength="8" @blur="deadZoneInputOnBlur($event, true)" @input="deadZoneInputOnInput($event, true)" @keydown.enter="($event.target as HTMLInputElement).blur()" />
        </template>
        <template v-if="showBottom">
          <span v-if="showTop" class="separator">|</span>
          <input v-model="deadZoneBottomMM" maxlength="8" @blur="deadZoneInputOnBlur($event, false)" @input="deadZoneInputOnInput($event, false)" @keydown.enter="($event.target as HTMLInputElement).blur()" />
        </template>
        mm
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Fe87v2DeviceList } from '@/define/device_vendor'
import { speciaBehiver, specialCheckCurrent } from '@/define/limit'
import { travelConfig } from '@/define/magnet_travel'
import { DeviceManager } from '@/service/device'
import { magnetSet } from '@/service/driver/magnet_set'
import firmware from '@/upgrade/firmware'
import { formatNumber, formatToFixedDecimal, textInputAllowOnlyNumbers } from '@libs/utils/input'
import { computed, onMounted, ref, watch, toRefs } from 'vue'

const props = defineProps({
  step: { type: Number, default: 0 },
  travelConfig: { type: Object, default: () => travelConfig(DeviceManager.instance.state.currentPid) },
  isSupportSingleKeyDeadZone: { type: Boolean, default: false },
  isSupportTopDeadZone: { type: Boolean, default: false },
  isSupportBottomDeadZone: { type: Boolean, default: false },
  isEnabled: { type: Boolean, default: false },
})

const emits = defineEmits(['selectCheck', 'mouseEnter', 'mouseLeave', 'change'])

const travel_config = computed(() => props.travelConfig as any)
const { Top, Bottom } = toRefs(magnetSet.newDeadZone)

const showTop = computed(() => props.isSupportTopDeadZone)
const showBottom = computed(() => props.isSupportBottomDeadZone)

const handleInput = () => {
  // 保持空，或仅用于触发特定逻辑。不再自动开启单键模式，以支持全局调节。
}

const minTopDeadZone = ref(0)
const maxTopDeadZone = ref(0)
const minBottomDeadZone = ref(0)
const maxBottomDeadZone = ref(0)

const fetchMinTopDeadZone = () => {
  if (specialCheckCurrent(speciaBehiver.DevicesOfSelfDevMix)) {
    firmware.fwVersionGreaterThanOrEqual('2.1.2', [...Fe87v2DeviceList]).then((res) => {
      minTopDeadZone.value = res ? 300 : 200
    })
  }
}

const initDeadZoneLimit = () => {
  const { deadZoneLimit, unit } = travel_config.value
  if (deadZoneLimit) {
    ;[minTopDeadZone.value, maxTopDeadZone.value, minBottomDeadZone.value, maxBottomDeadZone.value] = deadZoneLimit
  } else {
    maxTopDeadZone.value = unit
    maxBottomDeadZone.value = unit
  }
}

const deadZoneTopMM = ref('0.000')
const deadZoneBottomMM = ref('0.000')

const deadZoneInputOnBlur = (e: Event, isTop: boolean) => {
  const target = e.target as HTMLInputElement
  const setValue = formatToFixedDecimal(target, travel_config.value.decimalPlaces)
  if (isTop) {
    Top.value = setValue * travel_config.value.unit
  } else {
    Bottom.value = setValue * travel_config.value.unit
  }
}

const deadZoneInputOnInput = (e: Event, isTop: boolean) => {
  const defaultValue = isTop ? deadZoneTopMM.value : deadZoneBottomMM.value
  const value = textInputAllowOnlyNumbers(e, defaultValue)
  if (isTop) {
    deadZoneTopMM.value = value
  } else {
    deadZoneBottomMM.value = value
  }
  handleInput()
}

const updateDisplayValues = () => {
  const { unit, decimalPlaces } = travel_config.value
  deadZoneTopMM.value = formatNumber(Top.value / unit, decimalPlaces)
  deadZoneBottomMM.value = formatNumber(Bottom.value / unit, decimalPlaces)
}

const getAdaptiveFillHeight = (value: number) => {
  if (value <= 0 || travel_config.value.maxTravel <= 0) {
    return '0'
  }

  const ratio = Math.min(Math.max(value, 0), travel_config.value.maxTravel) / travel_config.value.maxTravel
  return `calc(${ratio} * (var(--mgx-track-height) - var(--mgx-thumb-size)) + var(--mgx-thumb-size))`
}

onMounted(() => {
  initDeadZoneLimit()
  updateDisplayValues()
  fetchMinTopDeadZone()
})

const applyDeadZoneLogic = (isTop: boolean) => {
  if (specialCheckCurrent(speciaBehiver.DeviceOfRoyuan)) {
    const val = isTop ? Top.value : Bottom.value
    Top.value = val
    Bottom.value = val
  }

  if (isTop) {
    if (Top.value <= minTopDeadZone.value) Top.value = minTopDeadZone.value
    if (Top.value > maxTopDeadZone.value) Top.value = maxTopDeadZone.value
    deadZoneTopMM.value = formatNumber(Top.value / travel_config.value.unit, travel_config.value.decimalPlaces)
  } else {
    if (Bottom.value <= minBottomDeadZone.value) Bottom.value = minBottomDeadZone.value
    if (Bottom.value > maxBottomDeadZone.value) Bottom.value = maxBottomDeadZone.value
    deadZoneBottomMM.value = formatNumber(Bottom.value / travel_config.value.unit, travel_config.value.decimalPlaces)
  }
}

watch(Top, () => applyDeadZoneLogic(true))
watch(Bottom, () => applyDeadZoneLogic(false))

const wheelEvent = (e: WheelEvent, setKey: string) => {
  e.preventDefault()
  handleInput()
  const increment = e.deltaY > 0 ? props.step : -props.step
  const max = travel_config.value.maxTravel || travel_config.value.unit
  if (setKey == 'top_dead_zone') {
    Top.value = Math.min(Math.max(Number(Top.value) + increment, 0), max)
  } else if (setKey == 'bottom_dead_zone') {
    Bottom.value = Math.min(Math.max(Number(Bottom.value) - increment, 0), max)
  }
}
</script>

<style lang="less" scoped>
.mgx_bar_item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  height: 100%;
  justify-content: center;
  flex: 1;
  min-width: 110px;
  position: relative;

  .progress_overlay {
    position: absolute;
    inset: 0;
    z-index: 100;
  }

  &.purple {
    --item-color: #bf81ff;
  }

  .visual_wrapper {
    width: 100%;
    height: calc(var(--mgx-track-height) + var(--mgx-thumb-size) + 8px);
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: visible;

    .bar_track {
      width: var(--mgx-thumb-size);
      height: var(--mgx-track-height);
      background: rgba(0, 0, 0, 0.04);
      border-radius: calc(var(--mgx-thumb-size) / 2);
      position: relative;
      border: none;
      box-shadow: none;
      overflow: visible;

      &::before {
        content: '';
        position: absolute;
        inset: 0;
        background-image: linear-gradient(to bottom, transparent, transparent 24.5%, rgba(0, 0, 0, 0.05) 25%, transparent 25.5%, transparent 49.5%, rgba(0, 0, 0, 0.05) 50%, transparent 50.5%, transparent 74.5%, rgba(0, 0, 0, 0.05) 75%, transparent 75.5%);
        z-index: 5;
      }

      .bar_fill {
        position: absolute;
        width: 100%;
        border-radius: 26px;
        overflow: hidden;
        background: var(--gradient-deadzone);

        &.top {
          top: 0;
        }
        &.bottom {
          bottom: 0;
        }
      }
    }

    .trip_range {
      position: absolute;
      width: var(--mgx-track-height);
      height: var(--mgx-thumb-size);
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%) rotate(90deg);
      cursor: grab;
      z-index: 20;
      appearance: none;
      background: transparent;
      border: none;
      padding: 0;
      margin: 0;
      pointer-events: none;

      &::-webkit-slider-thumb {
        appearance: none;
        width: var(--mgx-thumb-size);
        height: var(--mgx-thumb-size);
        background: #fff;
        border-radius: 50%;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
        cursor: grab;
        border: none;
        pointer-events: auto;
        transition: all 0.2s cubic-bezier(0.18, 0.89, 0.32, 1.28);
        background-image: radial-gradient(circle at center, var(--item-color) 0%, var(--item-color) 15%, #fff 16%, #fff 100%);
      }

      &:active::-webkit-slider-thumb {
        cursor: grabbing;
        transform: scale(1.08);
        box-shadow: 0 0 15px var(--item-color);
      }
    }
  }

  .bar_label_group {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding-top: 0px;
    background: transparent;
    transition: all 0.3s;

    .bar_label {
      font-size: 14px;
      color: var(--item-color);
      font-weight: 700;
      white-space: nowrap;
      letter-spacing: 0.2px;
      opacity: 0.95;
    }

    .bar_sub_label {
      font-size: 14px;
      color: var(--item-color);
      font-family: 'Inter', sans-serif;
      font-weight: 700;
      padding: 0;
      background: transparent;
      border-radius: 0;
      text-align: center;
      line-height: 1.2;
      display: flex;
      align-items: center;
      justify-content: center;

      input {
        width: 48px;
        background: transparent;
        border: none;
        color: inherit;
        font-family: inherit;
        font-size: inherit;
        font-weight: inherit;
        padding: 0;
        text-align: right;
        outline: none;
        border-bottom: 2px solid transparent;
        transition: all 0.2s;

        &:focus {
          border-bottom-color: var(--item-color);
        }

        &:hover {
          background: rgba(0, 0, 0, 0.05);
          border-radius: 4px;
        }
      }

      .separator {
        margin: 0 4px;
        opacity: 0.5;
        font-weight: 400;
      }
    }
  }

  &.disabled {
    opacity: 0.4;
    filter: grayscale(0.8);
    cursor: not-allowed;
    pointer-events: none;

    .bar_label_group {
      .bar_label,
      .bar_sub_label {
        color: var(--color-text-hint) !important;
      }
    }
  }
}
</style>
